import React, { useState, useEffect } from 'react';
import { Headphones, Play, Pause, Share2, Clock, ArrowRight, Volume2 } from 'lucide-react';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AUTHOR_IMG = "https://customer-assets-jai6qajn.emergentagent.net/job_cd729cf5-4469-4028-b6ba-a9c891131dbe/artifacts/dnctg08g_1780602847089.jpg";
const BOOK_COVER = "https://customer-assets.emergentagent.com/job_30b1d3cb-b799-4691-ba56-7e824b5598c4/artifacts/p185kuox_font%20cover%20jpg.jpg";

const EpisodePlayer = ({ episode }) => {
  const [playing, setPlaying] = useState(false);
  const [error, setError] = useState(false);
  const [audio] = useState(() => {
    const url = episode.audio_url || episode.external_audio_url;
    return url ? new Audio(url.startsWith('/') ? `${API_URL}${url}` : url) : null;
  });

  const toggle = () => {
    if (!audio) return;
    if (playing) audio.pause();
    else audio.play().catch(() => setError(true));
    setPlaying(!playing);
  };

  useEffect(() => {
    if (!audio) return;
    audio.onerror = () => setError(true);
    return () => { audio.pause(); audio.src = ''; };
  }, [audio]);

  return (
    <button onClick={toggle} disabled={!audio} className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${playing ? 'bg-gold text-navy' : 'bg-gold/10 text-gold/70 hover:bg-gold/20'} disabled:opacity-30`} data-testid={`play-ep-${episode.id}`}>
      {playing ? <Pause size={16} fill="currentColor" /> : <Play size={16} fill="currentColor" className="ml-0.5" />}
    </button>
  );
};

const PodcastPage = () => {
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_URL}/api/public/podcast`).then(r => r.json()).then(d => setEpisodes(d.episodes || [])).catch(() => {});
    setLoading(false);
  }, []);

  // Always show the original podcast as episode 0
  const defaultEpisode = {
    id: 'default',
    title: 'Cultural Fluency Without Compromise',
    description: 'Key insights from the book by Promise Monday.',
    audio_url: '',
    external_audio_url: 'https://customer-assets.emergentagent.com/job_db74d57d-6065-452a-a0c8-24e8297713b5/artifacts/e5jfnnn9_Cultural%20Fluency%20Without%20Compromise%20By%20Promise%20Monday%20Podcast%20mp3.MP3',
    featured_image: BOOK_COVER,
    date: '2025-12-01',
    episode_number: 0,
    status: 'published',
  };

  const allEpisodes = [defaultEpisode, ...episodes];

  return (
    <div className="bg-navy min-h-screen" data-testid="podcast-page">
      {/* Hero */}
      <section className="relative pt-32 pb-24 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none"><div className="absolute top-0 right-0 w-[50%] h-full bg-gradient-to-l from-gold/[0.02] to-transparent" /></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <span className="block text-[11px] tracking-[0.25em] uppercase text-gold font-sans font-semibold mb-6">Audio</span>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-[72px] text-cream font-light tracking-tighter leading-[0.92] mb-6">
            The <span className="text-gold">Podcast</span>
          </h1>
          <p className="text-cream/40 text-lg font-light leading-[1.8] tracking-wide max-w-md">
            Insights on cultural fluency, third culture leadership, and leading across difference.
          </p>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/15 to-transparent" />
      </section>

      {/* Episodes */}
      <section className="bg-navy-deep py-16">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          {loading ? <div className="py-16 text-center text-cream/20">Loading...</div> : (
            <div className="space-y-3">
              {allEpisodes.map((ep) => (
                <div key={ep.id} className="bg-navy border border-gold/[0.06] rounded-xl p-4 hover:border-gold/15 transition-all" data-testid={`episode-${ep.id}`}>
                  <div className="flex gap-4 items-center">
                    {/* Episode art */}
                    <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-navy-light">
                      {ep.featured_image ? (
                        <img src={ep.featured_image} alt={ep.title} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center"><Headphones size={20} className="text-cream/10" /></div>
                      )}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        {ep.episode_number != null && <span className="text-gold/40 text-[10px] font-semibold">EP {ep.episode_number}</span>}
                        {ep.date && <span className="text-cream/15 text-[10px]">{new Date(ep.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>}
                      </div>
                      <h3 className="text-cream text-sm font-semibold mb-0.5 truncate">{ep.title}</h3>
                      {ep.description && <p className="text-cream/30 text-xs line-clamp-1">{ep.description}</p>}
                    </div>

                    {/* Player */}
                    <EpisodePlayer episode={ep} />

                    {/* Share */}
                    <button onClick={() => navigator.clipboard.writeText(`${window.location.origin}/podcast`)} className="p-2 text-cream/15 hover:text-gold transition-colors hidden sm:block">
                      <Share2 size={14} />
                    </button>
                  </div>

                  {/* Show notes / transcript expand */}
                  {ep.show_notes && (
                    <div className="mt-3 pt-3 border-t border-cream/[0.03]">
                      <p className="text-cream/20 text-xs leading-relaxed">{ep.show_notes}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-navy py-14">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 text-center">
          <p className="text-cream/25 text-sm mb-4">Want to go deeper?</p>
          <Link to="/books" className="inline-flex items-center gap-2 px-6 py-3 bg-gold text-navy text-sm font-semibold rounded-full hover:bg-gold-light transition-all shadow-lg shadow-gold/20">
            Get the Book <ArrowRight size={14} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default PodcastPage;
